import { get, patch, post, put } from './request';

export interface LenderInsurance {
  id: string;
  estateId: string;
  estate?: {
    id: string;
    name: string;
    cnpj?: string;
    email?: string;
  };
  tenantName: string;
  tenantDocument: string;
  tenantEmail?: string;
  tenantPhone?: string;
  ownerName: string;
  ownerDocument: string;
  propertyAddress: string;
  propertyZipcode: string;
  propertyCity: string;
  propertyState: string;
  rentValue: number;
  insuranceValue?: number;
  status: 'ACTIVE' | 'CANCELLED';
  sourceType: 'BOND_INSURANCE' | 'FIRE_INSURANCE' | 'CAPITALIZATION' | 'STANDALONE';
  sourceId?: string;
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
}

export interface CreateLenderInsuranceRequest {
  estateId: string;
  tenantName: string;
  tenantDocument: string;
  tenantEmail?: string;
  tenantPhone?: string;
  ownerName: string;
  ownerDocument: string;
  propertyAddress: string;
  propertyZipcode: string;
  propertyCity: string;
  propertyState: string;
  rentValue: number;
  sourceType: 'BOND_INSURANCE' | 'FIRE_INSURANCE' | 'CAPITALIZATION' | 'STANDALONE';
  sourceId?: string;
}

// Batch interfaces
export interface LenderInsuranceBatch {
  id: string;
  estateId: string;
  estate?: { name: string };
  referenceMonth: number;
  referenceYear: number;
  totalItems: number;
  calculatedValue: number;
  adjustedValue?: number;
  dueDate?: string;
  boletoUrl?: string;
  status: 'PENDING' | 'BILLED' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  createdAt: string;
  items?: LenderInsuranceBatchItem[];
}

export interface LenderInsuranceBatchItem {
  id: string;
  batchId: string;
  lenderInsuranceId: string;
  lenderInsurance?: LenderInsurance;
  value: number;
}

export interface GenerateBatchRequest {
  estateId: string;
  referenceMonth: number;
  referenceYear: number;
}

// Insurance functions
export const createLenderInsurance = async (
  data: CreateLenderInsuranceRequest
): Promise<LenderInsurance> => {
  return await post('/lender-insurance', data);
};

export interface LenderInsuranceFilters {
  estateId?: string;
  search?: string;
  status?: string;
  sourceType?: string;
  page?: number;
  limit?: number;
}

export const getLenderInsurances = async (
  params: string | LenderInsuranceFilters
): Promise<{ data: LenderInsurance[]; count: number }> => {
  let queryString = '';

  if (typeof params === 'string') {
    queryString = `estateId=${params}`;
  } else {
    const query = new URLSearchParams();
    if (params.estateId) query.append('estateId', params.estateId);
    if (params.search) query.append('search', params.search);
    if (params.status) query.append('status', params.status);
    if (params.sourceType) query.append('sourceType', params.sourceType);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    queryString = query.toString();
  }

  return await get(`/lender-insurance?${queryString}`);
};

export const getLenderInsuranceById = async (
  id: string
): Promise<LenderInsurance> => {
  return await get(`/lender-insurance/${id}`);
};

export const cancelLenderInsurance = async (
  id: string
): Promise<LenderInsurance> => {
  return await patch(`/lender-insurance/${id}/cancel`, {});
};

// Batch functions
export interface LenderInsuranceBatchesFilters {
  estateId?: string;
  status?: string;
  month?: number;
  year?: number;
  page?: number;
  limit?: number;
}

export const getLenderInsuranceBatches = async (
  params?: string | LenderInsuranceBatchesFilters
): Promise<{ data: LenderInsuranceBatch[]; count: number }> => {
  let queryString = '';

  if (typeof params === 'string') {
    queryString = `?estateId=${params}`;
  } else if (params) {
    const query = new URLSearchParams();
    if (params.estateId) query.append('estateId', params.estateId);
    if (params.status) query.append('status', params.status);
    if (params.month) query.append('month', String(params.month));
    if (params.year) query.append('year', String(params.year));
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    queryString = `?${query.toString()}`;
  }

  return await get(`/lender-insurance-batches${queryString}`);
};

export const getLenderInsuranceBatchById = async (
  id: string
): Promise<LenderInsuranceBatch> => {
  return await get(`/lender-insurance-batches/${id}`);
};

export const generateLenderInsuranceBatch = async (
  data: GenerateBatchRequest
): Promise<{ batch: LenderInsuranceBatch; itemsCount: number; totalValue: number }> => {
  return await post('/lender-insurance-batches/generate', data);
};

export const billLenderInsuranceBatch = async (
  id: string,
  boletoUrl: string,
  dueDate?: string,
  adjustedValue?: number,
  status?: string
): Promise<LenderInsuranceBatch> => {
  return await put(`/lender-insurance-batches/${id}/bill`, { boletoUrl, dueDate, adjustedValue, status });
};

export const markBatchAsPaid = async (
  id: string
): Promise<LenderInsuranceBatch> => {
  return await put(`/lender-insurance-batches/${id}/pay`, {});
};

export const adjustBatchValue = async (
  id: string,
  adjustedValue: number
): Promise<LenderInsuranceBatch> => {
  return await put(`/lender-insurance-batches/${id}/adjust`, { adjustedValue });
};

export const downloadBatchSpreadsheet = (batchId: string): string => {
  return `${process.env.REACT_APP_API_URL}/lender-insurance-batches/${batchId}/spreadsheet`;
};

export interface LenderInsuranceSourceContext {
  route: string;
  queryParams?: Record<string, string>;
  stateParams?: Record<string, string>;
}

export const getLenderInsuranceSourceContext = async (
  id: string
): Promise<LenderInsuranceSourceContext | null> => {
  return await get(`/lender-insurance/${id}/source-context`);
};

export interface InsuranceBatchInfo {
  id: string;
  referenceMonth: number;
  referenceYear: number;
  status: string;
  calculatedValue: number;
  adjustedValue?: number;
  dueDate?: string;
  totalItems: number;
  estate?: { name: string };
  itemValue: number;
  createdAt: string;
}

export const getLenderInsuranceBatchesByInsuranceId = async (
  insuranceId: string
): Promise<InsuranceBatchInfo[]> => {
  return await get(`/lender-insurance/${insuranceId}/batches`);
};

